import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Lock, Download, Sparkles } from 'lucide-react';

// Button Component - Premium Design
export const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  className = '',
  disabled = false,
  onClick,
  ...props 
}) => {
  const baseClasses = "font-semibold tracking-tight transition-all duration-200 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]";
  
  const variants = {
    primary: "bg-gradient-to-r from-green-600 to-green-500 text-white hover:from-green-700 hover:to-green-600 shadow-lg shadow-green-500/25",
    secondary: "bg-white border-2 border-green-500 text-green-600 hover:bg-green-50",
    gold: "bg-gradient-to-r from-orange-500 to-orange-400 text-white hover:from-orange-600 hover:to-orange-500 shadow-lg shadow-orange-500/25",
    ghost: "bg-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-100",
    outline: "bg-white border border-gray-200 text-gray-700 hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50"
  };
  
  const sizes = {
    sm: "px-4 py-2 text-sm rounded-xl",
    md: "px-6 py-3 text-base rounded-xl",
    lg: "px-8 py-4 text-lg rounded-2xl",
    xl: "px-10 py-5 text-xl rounded-2xl"
  };
  
  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Card Component - Clean White
export const Card = ({ children, className = '', animated = true, ...props }) => {
  const baseClasses = "bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden";
  
  if (animated) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={`${baseClasses} ${className}`}
        {...props}
      >
        {children}
      </motion.div>
    );
  }
  
  return (
    <div className={`${baseClasses} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Input Component
export const Input = ({ 
  label, 
  placeholder, 
  value, 
  onChange, 
  className = '',
  ...props 
}) => {
  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
      )}
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-lg text-gray-900 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 focus:outline-none transition-all placeholder:text-gray-400"
        {...props}
      />
    </div>
  );
};

// Slider Component
export const Slider = ({ 
  label, 
  sublabel,
  value, 
  onChange, 
  min = 0, 
  max = 100,
  className = '' 
}) => {
  return (
    <div className={`grid grid-cols-[140px_1fr_50px] md:grid-cols-[180px_1fr_60px] items-center gap-3 md:gap-4 ${className}`}>
      <div className="text-sm text-gray-900 font-medium">
        {label}
        {sublabel && <span className="block text-xs text-gray-500 font-normal mt-1">{sublabel}</span>}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={onChange}
        className="w-full h-2 bg-gray-100 rounded-full appearance-none cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 
          [&::-webkit-slider-thumb]:bg-orange-500 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer
          [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md
          [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110"
      />
      <div className="text-right font-bold text-xl text-orange-500">
        {value}%
      </div>
    </div>
  );
};

// Chip Component (for single select)
export const Chip = ({ 
  children, 
  selected, 
  onClick, 
  className = '' 
}) => {
  return (
    <button
      onClick={onClick}
      className={`px-5 py-2.5 rounded-full border-2 font-medium text-sm transition-all duration-200
        ${selected 
          ? 'bg-orange-500 border-orange-500 text-white' 
          : 'bg-white border-gray-200 text-gray-700 hover:border-orange-400 hover:bg-orange-50'
        } ${className}`}
    >
      {children}
    </button>
  );
};

// Toggle Button Component
export const ToggleButton = ({ 
  children, 
  selected, 
  onClick, 
  className = '' 
}) => {
  return (
    <button
      onClick={onClick}
      className={`px-6 py-3 rounded-full border-2 font-medium text-sm transition-all duration-200
        ${selected 
          ? 'bg-green-500 border-green-500 text-white' 
          : 'bg-white border-gray-200 text-gray-700 hover:border-green-400 hover:bg-green-50'
        } ${className}`}
    >
      {children}
    </button>
  );
};

// Option Card Component
export const OptionCard = ({ 
  icon, 
  title, 
  description, 
  selected, 
  onClick,
  className = '' 
}) => {
  return (
    <button
      onClick={onClick}
      className={`w-full p-4 md:p-5 rounded-2xl border-2 text-left transition-all duration-200
        ${selected 
          ? 'border-green-500 bg-green-50' 
          : 'border-gray-100 bg-white hover:border-orange-300 hover:bg-orange-50'
        } ${className}`}
    >
      {icon && <span className="text-2xl mb-2 block">{icon}</span>}
      <span className={`block font-semibold text-sm md:text-base ${selected ? 'text-green-700' : 'text-gray-900'}`}>
        {title}
      </span>
      {description && (
        <span className="block text-xs md:text-sm text-gray-500 mt-1 leading-relaxed">
          {description}
        </span>
      )}
    </button>
  );
};

// Progress Bar Component
export const ProgressBar = ({ progress, className = '' }) => {
  return (
    <div className={`h-1.5 bg-gray-100 w-full overflow-hidden rounded-full ${className}`}>
      <motion.div
        className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"
        initial={{ width: 0 }}
        animate={{ width: `${progress}%` }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      />
    </div>
  );
};

// Score Ring Component
export const ScoreRing = ({ score, color = '#22C55E', size = 120 }) => {
  const circumference = 2 * Math.PI * 52;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox="0 0 120 120" className="transform -rotate-90 w-full h-full">
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke="#F3F4F6"
          strokeWidth="10"
        />
        <motion.circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          initial={{ strokeDasharray: circumference, strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className="text-4xl md:text-5xl font-extrabold"
          style={{ color }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          {score}
        </motion.span>
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">/ 100</span>
      </div>
    </div>
  );
};

// Axis Card Component
export const AxisCard = ({ zone, status, note, level }) => {
  const levelColors = {
    good: { border: 'border-l-green-500', bg: 'bg-green-50', text: 'text-green-700' },
    warn: { border: 'border-l-orange-500', bg: 'bg-orange-50', text: 'text-orange-700' },
    bad: { border: 'border-l-red-500', bg: 'bg-red-50', text: 'text-red-700' }
  };
  
  const colors = levelColors[level] || levelColors.warn;
  
  return (
    <div className={`bg-white rounded-2xl border border-gray-100 p-5 border-l-4 ${colors.border}`}>
      <div className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
        {zone}
      </div>
      <div className={`text-sm font-bold mb-2 ${colors.text}`}>
        {status}
      </div>
      <p className="text-xs text-gray-500 leading-relaxed">
        {note}
      </p>
    </div>
  );
};

// Priority Item Component
export const PriorityItem = ({ number, title, description }) => {
  const colors = ['#EF4444', '#F97316', '#EAB308', '#22C55E'];
  const bgColor = colors[Math.min(number - 1, 3)];
  
  return (
    <div className="flex gap-4 bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
        style={{ backgroundColor: bgColor }}
      >
        {number}
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
        <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
      </div>
    </div>
  );
};

// Alert Component
export const Alert = ({ children, variant = 'info', className = '' }) => {
  const variants = {
    success: 'bg-green-50 border-green-200 text-green-700',
    warning: 'bg-orange-50 border-orange-200 text-orange-700',
    error: 'bg-red-50 border-red-200 text-red-700',
    info: 'bg-gray-50 border-gray-200 text-gray-700'
  };
  
  return (
    <div className={`p-4 rounded-xl border text-sm leading-relaxed ${variants[variant]} ${className}`}>
      {children}
    </div>
  );
};

// Badge Component
export const Badge = ({ children, variant = 'gold', className = '' }) => {
  const variants = {
    gold: 'bg-orange-100 text-orange-600 border-orange-200',
    green: 'bg-green-100 text-green-600 border-green-200',
    dark: 'bg-gray-900 text-white border-transparent'
  };
  
  return (
    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide border ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

// Navigation Buttons
export const NavigationButtons = ({ onBack, onNext, backLabel = 'Retour', nextLabel = 'Continuer', showBack = true, nextDisabled = false }) => {
  return (
    <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
      {showBack ? (
        <Button variant="ghost" onClick={onBack}>
          <ChevronLeft className="w-4 h-4" />
          {backLabel}
        </Button>
      ) : (
        <div />
      )}
      <Button variant="primary" onClick={onNext} disabled={nextDisabled}>
        {nextLabel}
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

// Page Wrapper with Animation
export const PageWrapper = ({ children, className = '' }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Loading Spinner
export const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'w-5 h-5',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };
  
  return (
    <div className={`${sizes[size]} ${className}`}>
      <motion.div
        className="w-full h-full border-3 border-orange-200 border-t-orange-500 rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
    </div>
  );
};
